/**
 * ReasonConfirmDialog — shared admin confirm modal with required `reason` field.
 *
 * Estratto 27/05/26 (admin SUPER-MVP M2 quick actions) dal pattern duplicato
 * in AdminCustomerDetailPage, AdminUserDetailPage, AdminSubscriptionDetailPage,
 * AdminPositionDetailPage, AdminPostingDetailPage (5 copie identiche).
 *
 * Use cases: extend trial, suspend, soft delete, change plan, ecc. — qualsiasi
 * mutation admin che richiede reason in audit log.
 *
 * Behavior:
 *  - reason field obbligatorio (min 1 char dopo trim)
 *  - busy state during submit (disabilita bottone, mostra spinner)
 *  - on success: chiude modal + reset reason
 *  - on error: toast.error con message
 *
 * Per delete permanenti (typing del nome richiesto), usa PermanentDeleteDialog.
 */
import { useEffect, useState } from 'react';
import { ShieldAlert, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface ReasonConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  /** Optional extra fields (e.g. number input for "Extend trial by N days"). */
  extraFields?: React.ReactNode;
  onConfirm: (reason: string) => Promise<void>;
}

export function ReasonConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive = false,
  extraFields,
  onConfirm,
}: ReasonConfirmDialogProps) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open]);

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message ?? 'Operation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {destructive ? (
              <ShieldAlert className="h-4 w-4 text-destructive" />
            ) : (
              <ShieldCheck className="h-4 w-4" />
            )}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        {extraFields && <div className="space-y-3 py-2">{extraFields}</div>}

        <div className="space-y-2">
          <Label htmlFor="reason">Reason (required)</Label>
          <Input
            id="reason"
            placeholder="Brief reason for audit log..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            autoFocus
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            onClick={submit}
            disabled={busy || !reason.trim()}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
